import { LetterTypes, Sources } from '../generator/types';

import {
  getOrderedSources,
  resolveBlockSet,
  resolveParagraphContact,
  resolveParagraphs,
  resolveParagraphText,
} from './blockResolver';
import { LetterBlock, ParagraphTemplate } from './blocks';
import { getLetterFixedText } from './letterFixedText';
import { PricingMode } from './types';

export interface LetterBodyInput {
  letterType: LetterTypes;
  pricingMode: PricingMode;
  isJuvenile: boolean;
  selectedSources: Sources[];
  selectedRiders: string[];
  fieldValues: Record<string, string>;
}

export interface BuildOptions {
  // When true, per-block paragraph/contact text overrides
  // (__para_<id>_<idx> / __contact_<id>_<idx>) are honored. The drawer's PDF
  // upload path (DrawerPdfLetter) uses `true` so user edits in the cards
  // make it into the generated PDF. The legacy modal path (DrawerLetterBody
  // via *LetterContent wrappers) uses `false` — the modal has no override
  // mechanism, so honoring them there would diverge from trunk.
  allowOverrides: boolean;
}

export interface RenderableParagraph {
  text: string;
  contact?: string;
  // Block the paragraph came from. Fixed intro / closing text has none.
  blockId?: Sources;
}

export function collectFieldPlaceholders(
  blocks: LetterBlock[],
): Record<string, string> {
  const map: Record<string, string> = {};
  for (const block of blocks) {
    for (const p of block.paragraphs) {
      for (const f of p.fields) map[f.key] = f.placeholder;
    }
    for (const p of block.juvenileParagraphs ?? []) {
      for (const f of p.fields) if (!map[f.key]) map[f.key] = f.placeholder;
    }
  }
  return map;
}

// Sentinel emitted in 'sentinel' mode. The drawer card strips this and renders
// the "In addition," label as a separate visual badge above the paragraph.
export const IN_ADDITION_SENTINEL = '__INADDITION__';

export interface ApplyInAdditionOptions {
  // 'inline' (default): returns `"In addition, ..."`. Used by PDF/copy/preview.
  // 'sentinel': returns `IN_ADDITION_SENTINEL + ...`. Used by the editable
  //   drawer card so it can render the label as a chip outside the paragraph.
  mode?: 'inline' | 'sentinel';
}

export function applyInAdditionPrefix(
  text: string,
  blockIndex: number,
  paraIndex: number,
  options: ApplyInAdditionOptions = {},
): string {
  if (blockIndex === 0 || paraIndex !== 0) return text;
  const firstChar = text.charAt(0);
  if (!firstChar) return text;
  // Inline path (PDF/copy) skips the prefix when the paragraph already starts
  // with a non-uppercase character — preserves legacy "don't double-prefix"
  // behavior. Sentinel path always applies (drawer card renders the badge
  // independently of the text capitalization).
  const isSentinel = options.mode === 'sentinel';
  if (!isSentinel && firstChar !== firstChar.toUpperCase()) return text;
  const tail = `${firstChar.toLowerCase()}${text.slice(1)}`;
  return isSentinel ? `${IN_ADDITION_SENTINEL}${tail}` : `In addition, ${tail}`;
}

export function resolveOrderedBlocks(
  letterType: LetterTypes,
  pricingMode: PricingMode,
  selectedSources: Sources[],
): { orderedIds: Sources[]; orderedBlocks: LetterBlock[] } {
  const blockSet = resolveBlockSet(letterType, pricingMode);
  const orderedIds = getOrderedSources(blockSet, selectedSources);
  const orderedBlocks = orderedIds
    .map(id => blockSet[id])
    .filter((b): b is LetterBlock => b !== undefined);
  return { orderedIds, orderedBlocks };
}

export function buildLetterParagraphs(
  input: LetterBodyInput,
  options: BuildOptions,
): RenderableParagraph[] {
  const { orderedIds, orderedBlocks } = resolveOrderedBlocks(
    input.letterType,
    input.pricingMode,
    input.selectedSources,
  );

  const fixed = getLetterFixedText({
    letterType: input.letterType,
    selectedSources: orderedIds,
    isJuvenile: input.isJuvenile,
    hasRiders: input.selectedRiders.length > 0,
    pricingMode: input.pricingMode,
  });

  const out: RenderableParagraph[] = [];

  for (const p of fixed.preBlocks) {
    out.push({ text: resolveParagraphText(p, input.isJuvenile) });
  }

  orderedBlocks.forEach((block, blockIdx) => {
    const paragraphs = resolveParagraphs(block, input.isJuvenile);
    paragraphs.forEach((p: ParagraphTemplate, paraIdx) => {
      let text: string;
      const paraKey = `__para_${block.id}_${paraIdx}`;
      const overrideText = options.allowOverrides
        ? input.fieldValues[paraKey]
        : undefined;
      if (overrideText !== undefined) {
        text = overrideText;
      } else {
        const rawText = resolveParagraphText(p, input.isJuvenile);
        text = applyInAdditionPrefix(rawText, blockIdx, paraIdx);
      }

      const contactKey = `__contact_${block.id}_${paraIdx}`;
      const contactOverride = options.allowOverrides
        ? input.fieldValues[contactKey]
        : undefined;
      const contact =
        contactOverride ?? resolveParagraphContact(p, input.isJuvenile);

      out.push({ text, contact, blockId: block.id });
    });
  });

  for (const p of fixed.postBlocks) {
    out.push({ text: resolveParagraphText(p, input.isJuvenile) });
  }

  return out;
}
